import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PlatformMetrics = () => {
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [animatedMetrics, setAnimatedMetrics] = useState({
    totalCampaigns: 0,
    verifiedCreators: 0,
    brandRetention: 0,
    paymentSecurity: 0,
    fraudPrevention: 0,
    avgEarnings: 0,
    engagementRate: 0,
    culturalResonance: 0
  });

  const targetMetrics = {
    totalCampaigns: 12847,
    verifiedCreators: 8934,
    brandRetention: 94,
    paymentSecurity: 99.9,
    fraudPrevention: 99.7,
    avgEarnings: 3247,
    engagementRate: 8.5,
    culturalResonance: 92
  };

  const metricsContent = {
    en: {
      title: "Platform Trust & Performance",
      subtitle: "Real-time metrics showcasing our commitment to creator success and brand satisfaction",
      trustSignals: {
        title: "Trust & Security",
        metrics: [
          {
            key: 'totalCampaigns',
            label: 'Total Campaigns Completed',
            value: animatedMetrics?.totalCampaigns,
            suffix: '+',
            icon: 'CheckCircle',
            color: 'text-success'
          },
          {
            key: 'verifiedCreators',
            label: 'Verified Creators',
            value: animatedMetrics?.verifiedCreators,
            suffix: '+',
            icon: 'Shield',
            color: 'text-primary'
          },
          {
            key: 'brandRetention',
            label: 'Brand Retention Rate',
            value: animatedMetrics?.brandRetention,
            suffix: '%',
            icon: 'TrendingUp',
            color: 'text-accent'
          },
          {
            key: 'paymentSecurity',
            label: 'Payment Security Score',
            value: animatedMetrics?.paymentSecurity,
            suffix: '%',
            icon: 'Lock',
            color: 'text-success'
          }
        ]
      },
      performance: {
        title: "Performance Excellence",
        metrics: [
          {
            key: 'fraudPrevention',
            label: 'Fraud Prevention Rate',
            value: animatedMetrics?.fraudPrevention,
            suffix: '%',
            icon: 'AlertTriangle',
            color: 'text-warning'
          },
          {
            key: 'avgEarnings',
            label: 'Avg Creator Monthly Earnings',
            value: animatedMetrics?.avgEarnings,
            prefix: '$',
            icon: 'DollarSign',
            color: 'text-success'
          },
          {
            key: 'engagementRate',
            label: 'Average Engagement Rate',
            value: animatedMetrics?.engagementRate,
            suffix: '%',
            icon: 'Heart',
            color: 'text-error'
          },
          {
            key: 'culturalResonance',
            label: 'Cultural Resonance Score',
            value: animatedMetrics?.culturalResonance,
            suffix: '%',
            icon: 'Globe',
            color: 'text-primary'
          }
        ]
      },
      cta: "View Detailed Analytics",
      lastUpdated: "Last updated: 2 minutes ago"
    },
    ar: {
      title: "ثقة المنصة والأداء",
      subtitle: "مقاييس فورية تُظهر التزامنا بنجاح المبدعين ورضا العلامات التجارية",
      trustSignals: {
        title: "الثقة والأمان",
        metrics: [
          {
            key: 'totalCampaigns',
            label: 'إجمالي الحملات المكتملة',
            value: animatedMetrics?.totalCampaigns,
            suffix: '+',
            icon: 'CheckCircle',
            color: 'text-success'
          },
          {
            key: 'verifiedCreators',
            label: 'المبدعون المعتمدون',
            value: animatedMetrics?.verifiedCreators,
            suffix: '+',
            icon: 'Shield',
            color: 'text-primary'
          },
          {
            key: 'brandRetention',
            label: 'معدل الاحتفاظ بالعلامات التجارية',
            value: animatedMetrics?.brandRetention,
            suffix: '%',
            icon: 'TrendingUp',
            color: 'text-accent'
          },
          {
            key: 'paymentSecurity',
            label: 'نقاط أمان الدفع',
            value: animatedMetrics?.paymentSecurity,
            suffix: '%',
            icon: 'Lock',
            color: 'text-success'
          }
        ]
      },
      performance: {
        title: "التميز في الأداء",
        metrics: [
          {
            key: 'fraudPrevention',
            label: 'معدل منع الاحتيال',
            value: animatedMetrics?.fraudPrevention,
            suffix: '%',
            icon: 'AlertTriangle',
            color: 'text-warning'
          },
          {
            key: 'avgEarnings',
            label: 'متوسط أرباح المبدع الشهرية',
            value: animatedMetrics?.avgEarnings,
            prefix: '$',
            icon: 'DollarSign',
            color: 'text-success'
          },
          {
            key: 'engagementRate',
            label: 'متوسط معدل التفاعل',
            value: animatedMetrics?.engagementRate,
            suffix: '%',
            icon: 'Heart',
            color: 'text-error'
          },
          {
            key: 'culturalResonance',
            label: 'نقاط الرنين الثقافي',
            value: animatedMetrics?.culturalResonance,
            suffix: '%',
            icon: 'Globe',
            color: 'text-primary'
          }
        ]
      },
      cta: "عرض التحليلات التفصيلية",
      lastUpdated: "آخر تحديث: منذ دقيقتين"
    }
  };

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language') || 'en';
    setCurrentLanguage(savedLanguage);
  }, []);

  useEffect(() => {
    const animateMetrics = () => {
      const duration = 2500;
      const steps = 50;
      const stepDuration = duration / steps;

      let currentStep = 0;
      const interval = setInterval(() => {
        currentStep++;
        const progress = currentStep / steps;
        const easeOutCubic = 1 - Math.pow(1 - progress, 3);

        setAnimatedMetrics({
          totalCampaigns: Math.floor(targetMetrics?.totalCampaigns * easeOutCubic),
          verifiedCreators: Math.floor(targetMetrics?.verifiedCreators * easeOutCubic),
          brandRetention: Math.floor(targetMetrics?.brandRetention * easeOutCubic),
          paymentSecurity: (targetMetrics?.paymentSecurity * easeOutCubic)?.toFixed(1),
          fraudPrevention: (targetMetrics?.fraudPrevention * easeOutCubic)?.toFixed(1),
          avgEarnings: Math.floor(targetMetrics?.avgEarnings * easeOutCubic),
          engagementRate: (targetMetrics?.engagementRate * easeOutCubic)?.toFixed(1),
          culturalResonance: Math.floor(targetMetrics?.culturalResonance * easeOutCubic)
        });

        if (currentStep >= steps) {
          clearInterval(interval);
          setAnimatedMetrics({
            ...targetMetrics,
            paymentSecurity: targetMetrics?.paymentSecurity?.toFixed(1),
            fraudPrevention: targetMetrics?.fraudPrevention?.toFixed(1),
            engagementRate: targetMetrics?.engagementRate?.toFixed(1)
          });
        }
      }, stepDuration);

      return () => clearInterval(interval);
    };

    const timer = setTimeout(animateMetrics, 1000);
    return () => clearTimeout(timer);
  }, []);

  const formatNumber = (num) => {
    if (typeof num === 'string') return num;
    return num?.toLocaleString();
  };

  const content = metricsContent?.[currentLanguage];

  return (
    <section className={`py-20 bg-card ${currentLanguage === 'ar' ? 'rtl' : 'ltr'}`}>
      <div className="container mx-auto px-4">
        <div className={`text-center mb-16 ${currentLanguage === 'ar' ? 'text-right' : 'text-left'}`}>
          <h2 className="text-4xl font-bold text-foreground mb-4">
            {content?.title}
          </h2>
          <p className="text-xl text-muted-foreground max-w-4xl mx-auto">
            {content?.subtitle}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 max-w-7xl mx-auto">
          {/* Trust & Security Metrics */}
          <div className="space-y-6">
            <div className={`flex items-center space-x-3 mb-8 ${currentLanguage === 'ar' ? 'flex-row-reverse space-x-reverse' : ''}`}>
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name="Shield" size={20} color="var(--color-primary)" />
              </div>
              <h3 className="text-2xl font-bold text-foreground">
                {content?.trustSignals?.title}
              </h3>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {content?.trustSignals?.metrics?.map((metric) => (
                <div key={metric?.key} className="card-elevated p-6 hover-lift">
                  <div className={`flex items-center space-x-3 mb-4 ${currentLanguage === 'ar' ? 'flex-row-reverse space-x-reverse' : ''}`}>
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                      metric?.color === 'text-success' ? 'bg-success/10' :
                      metric?.color === 'text-primary' ? 'bg-primary/10' :
                      metric?.color === 'text-accent' ? 'bg-accent/10' : 'bg-muted'
                    }`}>
                      <Icon 
                        name={metric?.icon} 
                        size={16} 
                        color={`var(--color-${metric?.color?.split('-')?.[1]})`} 
                      />
                    </div>
                    <div className="flex-1">
                      <div className={`text-2xl font-bold ${metric?.color}`}>
                        {metric?.prefix || ''}{formatNumber(metric?.value)}{metric?.suffix || ''}
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {metric?.label}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Performance Metrics */}
          <div className="space-y-6">
            <div className={`flex items-center space-x-3 mb-8 ${currentLanguage === 'ar' ? 'flex-row-reverse space-x-reverse' : ''}`}>
              <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
                <Icon name="TrendingUp" size={20} color="var(--color-accent)" />
              </div>
              <h3 className="text-2xl font-bold text-foreground">
                {content?.performance?.title}
              </h3>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {content?.performance?.metrics?.map((metric) => (
                <div key={metric?.key} className="card-elevated p-6 hover-lift">
                  <div className={`flex items-center space-x-3 mb-4 ${currentLanguage === 'ar' ? 'flex-row-reverse space-x-reverse' : ''}`}>
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                      metric?.color === 'text-success' ? 'bg-success/10' :
                      metric?.color === 'text-warning' ? 'bg-warning/10' :
                      metric?.color === 'text-error' ? 'bg-error/10' :
                      metric?.color === 'text-primary' ? 'bg-primary/10' : 'bg-muted'
                    }`}>
                      <Icon 
                        name={metric?.icon} 
                        size={16} 
                        color={`var(--color-${metric?.color?.split('-')?.[1]})`} 
                      />
                    </div>
                    <div className="flex-1">
                      <div className={`text-2xl font-bold ${metric?.color}`}>
                        {metric?.prefix || ''}{formatNumber(metric?.value)}{metric?.suffix || ''}
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {metric?.label}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="text-center mt-16">
          <div className="bg-muted rounded-lg p-8 max-w-2xl mx-auto">
            <div className={`flex items-center justify-center space-x-2 mb-4 ${currentLanguage === 'ar' ? 'flex-row-reverse space-x-reverse' : ''}`}>
              <div className="w-3 h-3 bg-success rounded-full animate-pulse" />
              <span className="text-sm text-muted-foreground">
                {content?.lastUpdated}
              </span>
            </div>
            
            <Link to="/analytics-command-center">
              <Button 
                variant="default" 
                size="lg"
                iconName="BarChart3"
                iconPosition={currentLanguage === 'ar' ? 'right' : 'left'}
              >
                {content?.cta}
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PlatformMetrics;